import { useQuery } from '@apollo/client';
import React, { FC, useEffect } from 'react';
import { WeekSelectorContainerStyled, LegendSquareStyled, LegendLineStyled, LegendRhombusStyled } from './styled';
import { ListSubHeadingStyled } from '../styled';
import {
  Box,
  Text,
  FormControl,
  Select,
  SelectStyled,
  SelectOptionStyled,
  TextFieldSize,
  TextFieldAppearance,
  theme,
} from '@bechtel/gds';
import { ThemeContext } from 'styled-components';
import { GET_PROJECT_WEEKS } from '@bechtel/client-gql';
import { ProjectWeek } from '@bechtel/types';
import { find, get } from 'lodash';
import { useAppUser, CacheKeys, ErrorRetry, useSessionStorage } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';

interface WeekSelectorProps {
  onSelectWeek?: (projectWeek: ProjectWeek) => void;
}

export const ChartLegend: FC = () => {
  const themeContext = React.useContext(ThemeContext);
  return (
    <Box flexDirection={{ sm: 'column' }} ml={{ sm: '200' }}>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="claimed-legend">
        <LegendSquareStyled style={{ backgroundColor: String(themeContext.colors.orange[300]) }}></LegendSquareStyled>
        <Text size="200" color="grey.1000">
          <Trans>Claimed</Trans>
        </Text>
      </Box>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="target-legend">
        <LegendSquareStyled style={{ backgroundColor: String(themeContext.colors.blue[300]) }}></LegendSquareStyled>
        <Text size="200" color="grey.1000">
          <Trans>Target</Trans>
        </Text>
      </Box>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="target-legend">
        <LegendSquareStyled style={{ backgroundColor: String(themeContext.colors.green[300]) }}></LegendSquareStyled>
        <Text size="200" color="grey.1000">
          <Trans>Approved</Trans>
        </Text>
      </Box>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="cumulative-legend">
        <LegendLineStyled></LegendLineStyled>
        <Text size="200" color="grey.1000">
          <Trans>Cumulative</Trans>
        </Text>
      </Box>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="current-progress-legend">
        <LegendRhombusStyled borderTopBottomColor={themeContext.colors.black}></LegendRhombusStyled>
        <Text size="200" color="grey.1000">
          <Trans>Current Progress Claimed</Trans>
        </Text>
      </Box>
      <Box my={{ sm: '300' }} alignItems={{ sm: 'center' }} data-testid="weekly-target-legend">
        <LegendRhombusStyled borderTopBottomColor={themeContext.colors.red['600']}></LegendRhombusStyled>
        <Text size="200" color="grey.1000">
          <Trans>Weekly Target</Trans>
        </Text>
      </Box>
    </Box>
  );
};

const ItemComponent = ({ item }: any) => {
  return (
    <Text family="graphik" size="400">
      <Trans>Week</Trans> {item.weekNumber} {item.isCurrent ? <Trans> (Current) </Trans> : ``}
    </Text>
  );
};

const SelectedItemComponent = ({ item }: any) => {
  return (
    <Text family="graphik" size="400">
      <Trans>Week</Trans> {item.weekNumber} {item.isCurrent ? <Trans> (Current) </Trans> : ``}
    </Text>
  );
};
export const WeekSelector: FC<WeekSelectorProps> = ({ onSelectWeek }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch } = useQuery<{ projectWeeks: ProjectWeek[] }>(GET_PROJECT_WEEKS, {
    variables: {
      id: user?.project?.id,
    },
  });
  const projectWeeks: ProjectWeek[] = get(data, 'projectConfigById.projectWeeks', []);
  const [initialSelectedWeek, setInitialSelectedWeek] = useSessionStorage<ProjectWeek>(
    CacheKeys.PlanningProgressSelectedWeek
  );

  useEffect(() => {
    if (initialSelectedWeek || !projectWeeks || projectWeeks.length == 0) return;
    const projectCurrentWeek = find(projectWeeks, (projectWeek) => projectWeek.isCurrent === true);
    const selectedProjectWeek = projectCurrentWeek ? projectCurrentWeek : projectWeeks[0];
    setInitialSelectedWeek(selectedProjectWeek);
  }, [data, initialSelectedWeek, setInitialSelectedWeek]);

  useEffect(() => {
    if (!initialSelectedWeek) return;
    onSelectWeek && onSelectWeek(initialSelectedWeek);
  }, [initialSelectedWeek]); //TODO: Investigate adding onSelectWeek as dependency breaks the prop propogation

  return (
    <WeekSelectorContainerStyled data-testid="workpackage-list-container">
      <ListSubHeadingStyled>
        <Trans>View Progress</Trans>
      </ListSubHeadingStyled>
      <Box px={{ sm: '500' }} pt={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
        {!loading && loading && (
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading project weeks</Trans>
          </ErrorRetry>
        )}
        {!error && (
          <FormControl label={t`Selected Week`} css={{ marginBottom: theme.spacings[500] }} data-testid="selected-week">
            <Select
              size={TextFieldSize.LARGE}
              appearance={TextFieldAppearance.NORMAL}
              items={projectWeeks}
              itemComponent={ItemComponent}
              selectedComponent={SelectedItemComponent}
              selectedItem={initialSelectedWeek}
              onSelectedItemChange={async (changes: any) => {
                setInitialSelectedWeek(changes.selectedItem);
              }}
              css={`
                ${SelectStyled} {
                  height: ${({ theme }) => theme.spacings[900]};
                }
                ${SelectOptionStyled} {
                  padding: ${({ theme }) => theme.spacings[200]} ${({ theme }) => theme.spacings[500]};
                }
              `}
              loading={loading}
            ></Select>
          </FormControl>
        )}
        <ChartLegend />
      </Box>
    </WeekSelectorContainerStyled>
  );
};
